import { useState } from "react";
import LoginPage from "./LoginPage";
import RequestAccessPage from "./RequestAccessPage";
import "./LandingPage.css";

const WHATSAPP_NUMBER = "+41779954435";
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER.replace("+", "")}?text=${encodeURIComponent("Hello, I'd like to request access to DALFA.")}`;

const CAPABILITIES = [
  {
    title: "Autonomous Content Creation",
    desc: "DALFA generates professional images, videos, and marketing copy — all tailored to your products, without human input.",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg>
    ),
    span: "wide",
  },
  {
    title: "Smart Publishing",
    desc: "Automatically publishes to Facebook, Telegram, and more — with quality validation before every post goes live.",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M22 2 11 13"/><path d="m22 2-7 20-4-9-9-4z"/></svg>
    ),
    span: "normal",
  },
  {
    title: "Predictive Analytics",
    desc: "A/B testing, performance ranking, and AI-driven scoring to identify what converts and what to kill.",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 3v18h18"/><path d="m19 9-5 5-4-4-3 3"/></svg>
    ),
    span: "normal",
  },
  {
    title: "Video Pipeline",
    desc: "End-to-end video production with AI generation, frame validation, intro guard, and hybrid fallback systems.",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="2" width="20" height="20" rx="2.18"/><path d="m10 8 6 4-6 4z"/></svg>
    ),
    span: "wide",
  },
  {
    title: "Competitive Intelligence",
    desc: "Reverse-engineer competitor ads, spy on their strategies, and generate superior campaigns automatically.",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
    ),
    span: "normal",
  },
  {
    title: "Self-Learning Engine",
    desc: "Learns from every campaign cycle. Adapts strategies, improves copy, and optimizes spend over time.",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 2a10 10 0 1 0 10 10"/><path d="M12 12 12 6"/><path d="M12 12 17 12"/></svg>
    ),
    span: "normal",
  },
];

export default function LandingPage() {
  const [view, setView] = useState("landing"); // landing | login | request

  if (view === "login") return <LoginPage onBack={() => setView("landing")} />;
  if (view === "request") return <RequestAccessPage onBack={() => setView("landing")} />;

  return (
    <div className="dalfa-landing" data-testid="landing-page">
      {/* Header */}
      <header className="dalfa-header" data-testid="landing-header">
        <div className="dalfa-header-inner">
          <div className="dalfa-header-brand">
            <img
              src="https://customer-assets-7cd3h4nn.emergentagent.net/job_668c4879-79ca-423e-b1cc-dd12ca40e1a2/artifacts/j1j0s0hk_1937.png"
              alt="DALPHA PRIME 2.0"
              className="dalfa-brand-img"
            />
          </div>
          <nav className="dalfa-header-nav">
            <button className="dalfa-nav-login" onClick={() => setView("login")} data-testid="landing-login-btn">
              Sign In
            </button>
            <button className="dalfa-nav-cta" onClick={() => setView("request")} data-testid="landing-request-btn">
              Request Access
            </button>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="dalfa-hero" data-testid="landing-hero">
        <div className="dalfa-hero-glow" />
        <span className="dalfa-hero-label">Autonomous Marketing Engine</span>
        <h1 className="dalfa-hero-title">
          E-Commerce Marketing,<br />
          <span className="dalfa-hero-accent">Automated.</span>
        </h1>
        <p className="dalfa-hero-subtitle">
          DALFA creates content, publishes campaigns, analyzes performance, and scales your store — while you focus on your business.
        </p>
        <div className="dalfa-hero-actions">
          <button className="dalfa-btn-primary" onClick={() => setView("request")} data-testid="hero-request-btn">
            Request Access
          </button>
          <button className="dalfa-btn-outline" onClick={() => setView("login")} data-testid="hero-login-btn">
            Sign In
          </button>
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="dalfa-btn-whatsapp" data-testid="hero-whatsapp-btn">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.5.5 0 0 0 .612.612l4.458-1.495A11.952 11.952 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-2.327 0-4.47-.774-6.197-2.079l-.433-.327-2.65.889.889-2.65-.327-.433A9.96 9.96 0 0 1 2 12C2 6.486 6.486 2 12 2s10 4.486 10 10-4.486 10-10 10z"/></svg>
            WhatsApp
          </a>
        </div>
      </section>

      {/* Capabilities */}
      <section className="dalfa-capabilities" data-testid="landing-capabilities">
        <span className="dalfa-section-label">Core Capabilities</span>
        <h2 className="dalfa-section-title">The Intelligence Behind Your Growth</h2>
        <div className="dalfa-bento">
          {CAPABILITIES.map((cap, i) => (
            <div key={i} className={`dalfa-bento-card ${cap.span === "wide" ? "dalfa-bento-wide" : ""}`} data-testid={`capability-card-${i}`}>
              <div className="dalfa-bento-icon">{cap.icon}</div>
              <h3 className="dalfa-bento-title">{cap.title}</h3>
              <p className="dalfa-bento-desc">{cap.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="dalfa-cta-section" data-testid="landing-cta-section">
        <div className="dalfa-cta-glow" />
        <h2 className="dalfa-cta-title">Ready to scale your e-commerce?</h2>
        <p className="dalfa-cta-desc">Request access to the private platform and let DALFA handle your marketing engine.</p>
        <div className="dalfa-cta-actions">
          <button className="dalfa-btn-primary dalfa-btn-lg" onClick={() => setView("request")} data-testid="cta-request-btn">
            Request Access
          </button>
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="dalfa-btn-outline dalfa-btn-lg" data-testid="cta-whatsapp-btn">
            Contact on WhatsApp
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="dalfa-footer" data-testid="landing-footer">
        <div className="dalfa-footer-inner">
          <div className="dalfa-footer-brand">
            <img
              src="https://customer-assets-7cd3h4nn.emergentagent.net/job_668c4879-79ca-423e-b1cc-dd12ca40e1a2/artifacts/j1j0s0hk_1937.png"
              alt="DALPHA PRIME 2.0"
              className="dalfa-brand-img dalfa-brand-img-sm"
            />
          </div>
          <p className="dalfa-footer-copy">&copy; {new Date().getFullYear()} DALPHA PRIME 2.0. Autonomous Marketing Engine.</p>
        </div>
      </footer>
    </div>
  );
}
